import { Injectable, NgZone, Inject, OnDestroy } from '@angular/core';
import { DOCUMENT } from '@angular/common';
import {
  COL_EMPTY_CLASS,
  applyStackedClass
} from '../utils/fluid-columns-stack';

/**
 * TOBE-18004 — keeps empty filler columns visually free.
 *
 * Watches every columns row that contains a `.col-empty` filler and marks it
 * `cols-stacked` while its real columns are folded, which is the cue base.scss
 * uses to drop the filler out of the layout. See the long note at the
 * `> .col-empty` rule in `src/styles/common/base.scss` for why this cannot be
 * done in CSS, and `utils/fluid-columns-stack.ts` for the detection itself.
 *
 * Self-starting like ScrollDirectionService: injecting it is enough. All DOM
 * work runs outside the Angular zone — it only ever toggles one class, and
 * nothing in Angular's change detection depends on it.
 */
@Injectable({
  providedIn: 'root'
})
export class FluidColumnsStackService implements OnDestroy {
  private readonly observedRows = new WeakSet<HTMLElement>();
  private resizeObserver: ResizeObserver | null = null;
  private mutationObserver: MutationObserver | null = null;
  private rescanHandle: number | null = null;

  constructor(
    private readonly ngZone: NgZone,
    @Inject(DOCUMENT) private readonly document: Document
  ) {
    this.start();
  }

  private start(): void {
    if (!('ResizeObserver' in window) || !('MutationObserver' in window)) {
      return;
    }

    this.ngZone.runOutsideAngular(() => {
      this.resizeObserver = new ResizeObserver((entries) => {
        for (const entry of entries) {
          applyStackedClass(entry.target as HTMLElement);
        }
      });

      // Formio renders columns long after bootstrap and re-renders them on every
      // page/tab change, so rows are discovered continuously rather than once.
      this.mutationObserver = new MutationObserver(() => this.scheduleScan());
      this.mutationObserver.observe(this.document.body, {
        childList: true,
        subtree: true
      });

      this.scan();
    });
  }

  /**
   * Coalesce the burst of mutations a formio render produces into one scan on
   * the next frame.
   */
  private scheduleScan(): void {
    if (this.rescanHandle !== null) {
      return;
    }
    this.rescanHandle = requestAnimationFrame(() => {
      this.rescanHandle = null;
      this.scan();
    });
  }

  private scan(): void {
    // Query the fillers and step up, rather than using `:has()` on the rows —
    // same result, no dependency on `:has()` support in the browser.
    const fillers = this.document.querySelectorAll<HTMLElement>(
      `.${COL_EMPTY_CLASS}`
    );

    fillers.forEach((filler) => {
      const row = filler.parentElement;
      if (!row) {
        return;
      }
      applyStackedClass(row);
      if (!this.observedRows.has(row)) {
        this.observedRows.add(row);
        this.resizeObserver?.observe(row);
      }
    });
  }

  ngOnDestroy(): void {
    if (this.rescanHandle !== null) {
      cancelAnimationFrame(this.rescanHandle);
      this.rescanHandle = null;
    }
    this.mutationObserver?.disconnect();
    this.resizeObserver?.disconnect();
  }
}
